import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import isEmpty from 'lodash.isempty';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import { useApplicantData } from '../../hooks/useApplicantData';
import { FormWrap } from '../FormContainer/FormContainer';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import PhoneField from '../Fields/PhoneField';
import {
  formatPhoneNumberAsString,
  phoneNumberToObjectHelper,
  validatePhoneNumber,
} from '../../util/helpers/phoneNumberHelpers';
import usePersonPhoneNumberDispatch from '../../hooks/usePersonPhoneNumberDispatch';
import useStepPost from '../../hooks/useStepPost';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import { selectCountries } from '../../store/selectors/commonSelectors';
import { selectPortalGatewayISOCodesListToIgnoreAreaCode } from '../../store/selectors/settingsSelectors';
import Paragraph from '../Paragraph/Paragraph';
import {
  iraPhoneNumberNotUSA,
  isIraAccount,
} from '../../util/helpers/iraAccountHelpers';
import { selectBasicAccount } from '../../store/selectors/userSelectors';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  BASIC_ACCOUNT_QUERY_DATA,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  IS_UPGRADE_FLOW,
} from '../../constants/sessionStorage';
import { getBasicAccount } from '../../store/actions/user/userActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import {
  APPLICANT_TYPES,
  APPLICATION_TYPE,
  BASIC_ACCOUNT_UPGRADE_TYPES,
} from '../../util/constants';
import { getValidationSchemaImpersonate } from '../../util/helpers/getValidationSchemaImpersonate';
import {
  APPLICATION_TYPE_IRA,
  APPLICATION_TYPE_UTMA,
} from '../../constants/accountTypes';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';

const PhoneNumberForm = ({
  nextPath,
  applicantType,
  onModalClose,
  formId,
  setEventType,
  eventType,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ApplicantData = useApplicantData(applicantType);
  const PersonPhone = getApplicantData(ApplicantData, 'Phone');
  const countries = useSelector(selectCountries);
  const selectedISOCodesListToIgnoreAreaCode = useSelector(
    selectPortalGatewayISOCodesListToIgnoreAreaCode,
  );
  const { ApplicationUid } = useApplicationUid();
  const isUpgradeFlow = retrieveFromSessionStorage(IS_UPGRADE_FLOW);
  const basicAccountQueryData = retrieveFromSessionStorage(
    BASIC_ACCOUNT_QUERY_DATA,
  );
  const Application = useSelector(selectApplication);
  const { AccountUpgradeType } = Application;
  const isUpgrade =
    AccountUpgradeType === BASIC_ACCOUNT_UPGRADE_TYPES.IN_PROCESS;

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [defaultValue, setDefaultValue] = useState();
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );

  const { personPhoneDispatch } = usePersonPhoneNumberDispatch();
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (
      applicantType === APPLICANT_TYPES.MAIN &&
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Phone Number',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
      });
      return;
    }
    if (ApplicantData?.PersonName && ApplicantData?.PersonBirthday) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Phone Number Second Owner',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    if (applicantType === APPLICANT_TYPES.MAIN) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_COMPLETE,
        stepLabel: 'Secure Phone Number',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
      });
      return;
    }
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Secure Phone Number Second Owner',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
    });
  };

  useEffect(() => {
    const { ApplicationType } = Application;
    if (
      isUpgradeFlow &&
      basicAccountQueryData?.accountUid &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_UTMA &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_IRA
    ) {
      dispatch(
        getBasicAccount({
          applicationUid: ApplicationUid,
          accountUid: basicAccountQueryData?.accountUid,
        }),
      );
      return;
    }
    if (
      isUpgrade &&
      Application?.AccountUid &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_UTMA &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_IRA
    ) {
      dispatch(
        getBasicAccount({
          applicationUid: ApplicationUid,
          accountUid: Application?.AccountUid,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const formValidationSchema = Yup.object({
    ...getValidationSchema({
      fields: ['PersonPhoneNumber'],
    }),
  });

  const formValidationSchemaImpersonate = Yup.object({
    ...getValidationSchemaImpersonate({
      fields: ['PersonPhoneNumber'],
    }),
  });
  const basicAccountData = useSelector(selectBasicAccount);

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    formState: { errors },
    getValues,
    // reset,
  } = useForm({
    resolver: yupResolver(
      isSalesAssisted ? formValidationSchemaImpersonate : formValidationSchema,
    ),
    defaultValues: '',
  });

  const { onSuccess } = useStepPost({
    nextPath,
    onModalClose,
  });

  const setFormValue = (label, value) => {
    if (value) {
      setValue(label, value, { shouldDirty: true });
    }
  };

  useEffect(() => {
    if (!isEmpty(PersonPhone?.LocalNumber)) {
      setDefaultValue(PersonPhone);
    }
    if (
      !isEmpty(
        basicAccountData?.Phone?.LocalNumber &&
          applicantType === APPLICANT_TYPES.MAIN &&
          !PersonPhone?.LocalNumber,
      )
    ) {
      setDefaultValue(basicAccountData.Phone);
    }

    return '';
  }, [PersonPhone, applicantType, basicAccountData]);

  useEffect(() => {
    if (
      countries &&
      (PersonPhone?.Iso3CountryCode ||
        (basicAccountData?.Phone?.Iso3CountryCode &&
          applicantType === APPLICANT_TYPES.MAIN))
    ) {
      if (PersonPhone?.LocalNumber) {
        setPhoneNumber(
          formatPhoneNumberAsString(
            PersonPhone,
            countries,
            selectedISOCodesListToIgnoreAreaCode,
          ),
        );
        return;
      }
      if (
        basicAccountData?.Phone?.LocalNumber &&
        !PersonPhone?.LocalNumber &&
        applicantType === APPLICANT_TYPES.MAIN
      ) {
        setPhoneNumber(
          formatPhoneNumberAsString(
            basicAccountData?.Phone,
            countries,
            selectedISOCodesListToIgnoreAreaCode,
          ),
        );
        return;
      }
      setPhoneNumber('');
    }
    // eslint-disable-next-line
  }, [
    PersonPhone,
    basicAccountData?.Phone,
    selectedISOCodesListToIgnoreAreaCode,
    phoneNumber,
  ]);

  useEffect(() => {
    setFormValue('PersonPhoneNumber', phoneNumber);
    // eslint-disable-next-line
  }, [phoneNumber]);

  const onAPISuccess = () => {
    onSuccess();
    setIsDisabled(false);
  };

  const onSubmit = ({ PersonPhoneNumber }) => {
    const onError = (message) => {
      setError('PersonPhoneNumber', {
        type: 'string',
        message,
      });
    };
    if (isSalesAssisted && !PersonPhoneNumber) {
      personPhoneDispatch({
        personUid: ApplicantData?.PersonUid,
        defaultValues: defaultValue,
        data: {
          PersonPhoneNumberFormatted: {
            AreaCode: null,
            Iso3CountryCode: null,
            LocalNumber: null,
            StateCode: null,
          },
        },
        onSuccess:
          eventType === 'Form'
            ? () => onAPISuccess()
            : () => {
                setHasError(false);
                setSaveAndCloseModalOpen(true);
              },
        onError:
          eventType === 'Form'
            ? () => onError()
            : () => {
                setHasError(true);
                setSaveAndCloseModalOpen(true);
              },
      });
      return;
    }
    const PersonPhoneNumberTrim = `+${PersonPhoneNumber.replace(
      /[^\w\s]/g,
      '',
    )}`;
    if (!ApplicantData?.PersonUid || !PersonPhoneNumberTrim) {
      return;
    }

    if (PersonPhoneNumber && validatePhoneNumber(PersonPhoneNumberTrim)) {
      setError('PersonPhoneNumber', {
        type: 'manual',
        message: t('fields.errors.validation.personPhoneIsInvalid'),
      });
      return;
    }

    const PersonPhoneNumberFormatted = phoneNumberToObjectHelper(
      PersonPhoneNumberTrim,
    );

    if (iraPhoneNumberNotUSA(PersonPhoneNumberFormatted)) {
      setError('PersonPhoneNumber', {
        type: 'manual',
        message: t('fields.errors.validation.personPhoneNotUSA'),
      });
      return;
    }

    setIsDisabled(true);

    personPhoneDispatch({
      personUid: ApplicantData?.PersonUid,
      defaultValues: defaultValue,
      data: {
        PersonPhoneNumberFormatted,
      },
      onSuccess:
        eventType === 'Form'
          ? () => onAPISuccess()
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError:
        eventType === 'Form'
          ? () => onError()
          : () => {
              setHasError(true);
              setSaveAndCloseModalOpen(true);
            },
    });
    handleGtmEvent();
  };

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        render={({ field, fieldState }) => (
          <PhoneField
            {...field}
            {...fieldState}
            label={t('fields.labels.personPhone')}
            setValue={setValue}
            errors={errors}
            defaultValue={getValues('PersonPhoneNumber')}
            isIraAccount={isIraAccount()}
            isPhoneConnectedTo2FA={
              basicAccountData?.IsPhoneConnectedTo2FA &&
              applicantType === APPLICANT_TYPES.MAIN
            }
          />
        )}
        name="PersonPhoneNumber"
      />
      {isIraAccount() && (
        <Paragraph mt={12}>
          <strong>{t('common.note')}</strong>: {t('securePhoneNumber.note')}
        </Paragraph>
      )}
      <OnBoardingCta
        type="submit"
        onClick={() => setEventType('Form')}
        title={nextPath ? t('continue') : t('update')}
        disabled={isDisabled}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={() => onAPISuccess()}
        />
      )}
    </FormWrap>
  );
};

PhoneNumberForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  skipStepPath: PropTypes.string,
  dependentSkipStepPath: PropTypes.string,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default PhoneNumberForm;
